import { useQuery } from '@tanstack/react-query'
import { getOrdersWithContacts, getOrdersSummary, type OrderWithContact } from '@/data/mockOrdersData'

export interface OrdersFilters {
	stage?: string[]
	search?: string
	dateFrom?: string
	dateTo?: string
	location?: string
	isNewCustomer?: boolean
	source?: string
}

export interface OrdersSortOptions {
	field: 'created_at' | 'updated_at' | 'stage' | 'contact.display_name' | 'value_cents'
	direction: 'asc' | 'desc'
}

export interface OrdersQueryParams {
	filters?: OrdersFilters
	sort?: OrdersSortOptions
	page?: number
	limit?: number
}

export interface OrdersQueryResult {
	orders: OrderWithContact[]
	total: number
	page: number
	limit: number
	hasMore: boolean
}

// Mock API simulation with realistic delays
const mockOrdersApi = async (params: OrdersQueryParams = {}): Promise<OrdersQueryResult> => {
	// Simulate network delay
	await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300))
	
	let orders = getOrdersWithContacts()
	
	// Apply filters
	if (params.filters) {
		const { stage, search, dateFrom, dateTo, location, isNewCustomer, source } = params.filters
		
		if (stage && stage.length > 0) {
			orders = orders.filter(order => stage.includes(order.stage))
		}
		
		if (search) {
			const searchLower = search.toLowerCase()
			orders = orders.filter(order => 
				order.contact.display_name.toLowerCase().includes(searchLower) ||
				order.contact.email?.toLowerCase().includes(searchLower) ||
				order.contact.phone_raw?.includes(search)
			)
		}
		
		if (dateFrom) {
			orders = orders.filter(order => new Date(order.created_at) >= new Date(dateFrom))
		}
		
		if (dateTo) {
			orders = orders.filter(order => new Date(order.created_at) <= new Date(dateTo))
		}
		
		if (location) {
			orders = orders.filter(order => order.location_id === location)
		}
		
		if (isNewCustomer !== undefined) {
			orders = orders.filter(order => order.is_new_customer === isNewCustomer)
		}
		
		if (source) {
			orders = orders.filter(order => order.source.toLowerCase().includes(source.toLowerCase()))
		}
	}
	
	// Apply sorting
	if (params.sort) {
		const { field, direction } = params.sort
		orders.sort((a, b) => {
			let aVal: any, bVal: any
			
			switch (field) {
				case 'contact.display_name':
					aVal = a.contact.display_name
					bVal = b.contact.display_name
					break
				case 'created_at':
					aVal = new Date(a.created_at)
					bVal = new Date(b.created_at)
					break
				case 'updated_at':
					aVal = new Date(a.updated_at)
					bVal = new Date(b.updated_at)
					break
				case 'stage':
					// Custom stage ordering for meaningful sort (per PRD stages)
					const stageOrder = {
						'new_lead': 0,
						'qualified': 1,
						'scheduled': 2,
						'completed': 3,
						'lost': 4
					}
					aVal = stageOrder[a.stage as keyof typeof stageOrder] ?? 999
					bVal = stageOrder[b.stage as keyof typeof stageOrder] ?? 999
					break
				case 'value_cents':
					aVal = a.value_cents
					bVal = b.value_cents
					break
				default:
					aVal = a.created_at
					bVal = b.created_at
			}
			
			if (aVal < bVal) return direction === 'asc' ? -1 : 1
			if (aVal > bVal) return direction === 'asc' ? 1 : -1
			return 0
		})
	} else {
		// Default sort: most recent first
		orders.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
	}
	
	// Apply pagination
	const page = params.page || 1
	const limit = params.limit || 50
	const startIndex = (page - 1) * limit
	const endIndex = startIndex + limit
	const paginatedOrders = orders.slice(startIndex, endIndex)
	
	return {
		orders: paginatedOrders,
		total: orders.length,
		page,
		limit,
		hasMore: endIndex < orders.length
	}
}

export const useOrdersQuery = (params: OrdersQueryParams = {}) => {
	return useQuery({
		queryKey: ['orders', params],
		queryFn: () => mockOrdersApi(params),
		staleTime: 30000, // 30 seconds
		refetchOnWindowFocus: false,
	})
}

// Hook for orders summary/stats
export const useOrdersSummaryQuery = () => {
	return useQuery({
		queryKey: ['orders-summary'],
		queryFn: async () => {
			// Simulate network delay
			await new Promise(resolve => setTimeout(resolve, 150))
			return getOrdersSummary()
		},
		staleTime: 60000, // 1 minute
		refetchOnWindowFocus: false,
	})
}

// Hook for single order details
export const useOrderQuery = (orderId: string) => {
	return useQuery({
		queryKey: ['order', orderId],
		queryFn: async () => {
			// Simulate network delay
			await new Promise(resolve => setTimeout(resolve, 100))
			const orders = getOrdersWithContacts()
			const order = orders.find(o => o.id === orderId)
			if (!order) {
				throw new Error('Order not found')
			}
			return order
		},
		enabled: !!orderId,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	})
}
